// Geometry for the cold point chart (spec §9). Pure. A line never crosses a server-reported gap.
// The server may return more than maxBuckets buckets when there are many gaps (no-join beats the cap),
// so nothing here assumes a bucket count.
const r1 = (n) => Math.round(n * 10) / 10;

export function splitAtGaps(buckets, gaps, settingsSegments = []) {
  // Every settings-segment boundary is a zero-width gap: the line never joins across a version edge.
  const edges = settingsSegments.flatMap((s) => [s.fromMs, s.toMs]).map((ms) => ({ fromMs: ms, toMs: ms }));
  const sortedGaps = [...gaps, ...edges].sort((a, b) => a.fromMs - b.fromMs);
  const segments = [];
  let cur = [];
  let gi = 0;   // gaps are chronological and buckets too: one forward pass, not buckets x gaps
  for (const b of buckets) {
    if (cur.length) {
      const prev = cur[cur.length - 1];
      while (gi < sortedGaps.length && sortedGaps[gi].toMs <= prev.atMs) gi += 1;
      if (gi < sortedGaps.length && sortedGaps[gi].fromMs < b.atMs) {
        segments.push(cur);
        cur = [];
      }
    }
    cur.push(b);
  }
  if (cur.length) segments.push(cur);
  return segments;
}

export function buildChart({
  buckets, gaps, doors, moving, excursions, rangeMin, rangeMax, fromMs, toMs,
  width = 800, height = 220, pad = { l: 36, r: 8, t: 10, b: 22 },
  segments: settingsSegments = [],
}) {
  const plot = { x: pad.l, y: pad.t, w: width - pad.l - pad.r, h: height - pad.t - pad.b };
  const strip = { y: plot.y + plot.h + 2, h: Math.max(2, Math.min(6, pad.b - 4)) };
  // A null bound is never read as 0 (Number(null) === 0): only real numbers count.
  const bounds = [rangeMin, rangeMax, ...settingsSegments.filter((s) => s.enabled).flatMap((s) => [s.rangeMin, s.rangeMax])].filter((v) => typeof v === 'number');
  let lo = Infinity;
  let hi = -Infinity;
  for (const b of buckets) {
    if (b.min < lo) lo = b.min;
    if (b.max > hi) hi = b.max;
  }
  for (const v of bounds) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) { lo = 0; hi = 10; }
  if (lo === hi) { lo -= 1; hi += 1; }
  const padY = (hi - lo) * 0.1;
  const yLo = lo - padY;
  const yHi = hi + padY;
  const span = Math.max(1, toMs - fromMs);
  const x = (ms) => plot.x + ((Math.min(Math.max(ms, fromMs), toMs) - fromMs) / span) * plot.w;
  const y = (v) => plot.y + (1 - (v - yLo) / (yHi - yLo)) * plot.h;
  const pt = (b, v) => `${r1(x(b.atMs))},${r1(y(v))}`;

  const segments = splitAtGaps(buckets, gaps, settingsSegments).map((seg) => ({
    line: seg.map((b, i) => `${i ? 'L' : 'M'}${pt(b, b.avg)}`).join(' '),
    envelope: seg.length > 1
      ? [...seg.map((b, i) => `${i ? 'L' : 'M'}${pt(b, b.max)}`), ...[...seg].reverse().map((b) => `L${pt(b, b.min)}`), 'Z'].join(' ')
      : null,
    dot: seg.length === 1 ? { x: r1(x(seg[0].atMs)), y: r1(y(seg[0].avg)) } : null,
  }));

  const span2 = (from, to) => {
    const a = x(from);
    return { x: r1(a), w: r1(Math.max(1, x(to) - a)) };
  };
  const bandY = (min, max) => ({
    y1: r1(typeof max === 'number' ? y(max) : plot.y),
    y2: r1(typeof min === 'number' ? y(min) : plot.y + plot.h),
  });
  const hasBound = (min, max) => typeof min === 'number' || typeof max === 'number';
  // No segments = phase 1 behaviour: one full-width band from the point's current range.
  const bands = settingsSegments.length
    ? settingsSegments.filter((s) => s.enabled && hasBound(s.rangeMin, s.rangeMax))
      .map((s) => ({ ...span2(s.fromMs, s.toMs), ...bandY(s.rangeMin, s.rangeMax) }))
    : (hasBound(rangeMin, rangeMax) ? [{ x: plot.x, w: plot.w, ...bandY(rangeMin, rangeMax) }] : []);
  const band = bands[0] ?? null;
  const off = settingsSegments.filter((s) => !s.enabled).map((s) => span2(s.fromMs, s.toMs));
  const versionMarks = [];
  settingsSegments.forEach((s, i) => {
    if (i > 0 && s.versionNo != null && s.versionNo !== settingsSegments[i - 1].versionNo) versionMarks.push({ x: r1(x(s.fromMs)), versionNo: s.versionNo });
  });

  return {
    width, height, plot, strip, segments, band, bands, off, versionMarks,
    excursions: excursions.map((e) => ({ ...span2(Date.parse(e.startedAt), e.endedAt ? Date.parse(e.endedAt) : toMs), kind: e.kind })),
    gaps: gaps.map((g) => ({ ...span2(g.fromMs, g.toMs), kind: 'gap' })),
    moving: moving.map((m) => ({ ...span2(m.fromMs, m.toMs), kind: m.moving ? 'moving' : 'stopped' })),
    doors: doors.map((d) => ({ x: r1(x(d.atMs)), open: d.open })),
    yTicks: [lo, (lo + hi) / 2, hi].map((v) => ({ y: r1(y(v)), label: v.toFixed(1) })),
  };
}

/**
 * What the chart shows at time `ms`: the nearest bucket's reading, "no data" when `ms` falls in a
 * server-reported gap (or there are no buckets), plus a door mark if one is close (within `doorMs`).
 * Pure; the tooltip and crosshair read this.
 */
export function probeAt({ buckets: allBuckets = [], gaps = [], doors: allDoors = [], segments = [] }, ms, doorMs = 0) {
  // Inside an enabled segment, only that segment's buckets and door marks can answer: just after a re-enable the
  // nearest bucket may be from before the off stretch, and the tooltip must never carry a reading across it.
  const cur = segments.find((s) => s.enabled && ms >= s.fromMs && ms < s.toMs);
  const inCur = (atMs) => !cur || (atMs >= cur.fromMs && atMs < cur.toMs);
  const buckets = allBuckets.filter((b) => inCur(b.atMs));
  const doors = allDoors.filter((d) => inCur(d.atMs));
  const door = doors.reduce((best, d) => {
    const dist = Math.abs(d.atMs - ms);
    return dist <= doorMs && (!best || dist < best.dist) ? { open: d.open, atMs: d.atMs, dist } : best;
  }, null);
  const doorOut = door ? { open: door.open, atMs: door.atMs } : null;
  if (segments.some((s) => !s.enabled && ms >= s.fromMs && ms < s.toMs)) return { kind: 'off', atMs: ms, door: doorOut };
  if (gaps.some((g) => ms >= g.fromMs && ms <= g.toMs) || buckets.length === 0) {
    return { kind: 'gap', atMs: ms, door: doorOut };
  }
  let nearest = buckets[0];
  for (const b of buckets) if (Math.abs(b.atMs - ms) < Math.abs(nearest.atMs - ms)) nearest = b;
  return { kind: 'value', atMs: nearest.atMs, min: nearest.min, max: nearest.max, avg: nearest.avg, door: doorOut };
}
